import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { useStore } from 'stores/connect';
import Button from 'components/bootstrap/Button';
import Icon from 'components/common/Icon';

import UploadFieldStore, { UploadFieldActions } from './UploadField/UploadFieldStore';
import type { UploadFile } from './UploadField/Types';

type UploadCSVFileAdapterSummaryProps = {
  dataAdapter: {
    config: {
      file_id: string;
      separator: string;
      quotechar: string;
      key_column: string;
      value_column: string;
      check_interval: number;
      case_insensitive_lookup: boolean;
      multi_value_lookup: boolean;
      cidr_lookup: boolean;
    };
  };
};

const DownloadButton = styled(Button)`
  padding: 0;
  border: 0;
`;

const UploadCSVFileAdapterSummary = ({ dataAdapter }: UploadCSVFileAdapterSummaryProps) => {
  const { config } = dataAdapter;
  const valueLabel = config.multi_value_lookup ? 'Value columns' : 'Value column';
  const value = config.multi_value_lookup && !config.value_column ? 'All columns except the key' : config.value_column;
  const [isLoading, setIsLoading] = useState(true);
  const [currentFile, setCurrentFile] = useState<UploadFile | typeof undefined>();
  useStore(UploadFieldStore);

  const fileId = config.file_id;

  const _downloadFile = () => UploadFieldActions.downloadFile(fileId);

  useEffect(() => {
    if (!isLoading) {
      return;
    }

    UploadFieldActions.get(fileId)
      .then((response) => {
        setCurrentFile({
          id: fileId,
          name: response.file_name,
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [fileId, isLoading]);

  return (
    <dl>
      <dt>File name</dt>
      {!currentFile && <dd>Loading...</dd>}
      {currentFile && (
        <dd>
          <DownloadButton bsStyle="link" bsSize="small" onClick={_downloadFile}>
            {currentFile.name}
            <Icon name="download" style={{ marginLeft: 4 }} />
          </DownloadButton>
        </dd>
      )}
      <dt>Separator</dt>
      <dd>
        <code>{config.separator}</code>
      </dd>
      <dt>Quote character</dt>
      <dd>
        <code>{config.quotechar}</code>
      </dd>
      <dt>Key column</dt>
      <dd>{config.key_column}</dd>
      <dt>{valueLabel}</dt>
      <dd>{value}</dd>
      <dt>Check interval</dt>
      <dd>{config.check_interval} seconds</dd>
      <dt>Multi-value lookup</dt>
      <dd>{config.multi_value_lookup ? 'yes' : 'no'}</dd>
      <dt>Case-insensitive lookup</dt>
      <dd>{config.case_insensitive_lookup ? 'yes' : 'no'}</dd>
      <dt>CIDR lookup</dt>
      <dd>{config.cidr_lookup ? 'yes' : 'no'}</dd>
    </dl>
  );
};

export default UploadCSVFileAdapterSummary;
